/**
 * App — routing surface only. Heavy routes are lazy-loaded so the initial
 * login chunk stays lean.
 *
 * Public routes (no guard):
 *   /onboarding       → OnboardingPage (the emailed "Set up your account" link)
 *   /apply, /register → retired; redirect to https://www.drtutor.uk/learn#book
 *
 * Guest routes (signed-out only — bounce to / if signed in):
 *   /login            → LoginPage
 *   /signup           → SignupPage (accept-invite flow)
 *   /forgot-password  → ForgotPasswordPage
 *   /reset-password   → ResetPasswordPage
 *
 * Protected routes (signed-in only, bounce to /login if not), all rendered
 * inside one persistent <AppFrame> layout route:
 *   /  /lessons  /homework  /submissions  /resources  /students  /tutors
 *   /profile  /data-control  /change-password
 *
 *   *                 → redirect home
 *
 * The Toaster mounts once at the root so any component can dispatch a toast
 * without a provider above it.
 *
 * Speed: with a saved session, the current page's chunk starts downloading
 * at boot, alongside the session check rather than after it. Once the frame
 * is up, the remaining signed-in pages preload while the browser is idle, so
 * moving between them never waits on the network for code.
 */

import { lazy, Suspense, useEffect } from "react";
import { basePath } from "@/lib/tenant/basePath";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { GuestRoute } from "@/primitives/layout/GuestRoute";
import { ProtectedRoute } from "@/primitives/layout/ProtectedRoute";
import { ExternalRedirect } from "@/primitives/layout/ExternalRedirect";
import { RouteFallback } from "@/primitives/layout/RouteFallback";
import { FileViewerHost } from "@/components/viewer";
import { AppFrame } from "@/primitives/layout/AppFrame";
import { Toaster } from "@/primitives/ui/Toast";
import { tokenStorage } from "@/lib/api/tokens";
import { useAppliedTheme } from "@/lib/theme";
import { useTenant, useTenantState } from "@/lib/tenant";
import { RequireFeature } from "@/primitives/layout/RequireFeature";
import { SetupGate } from "@/components/tenant/SetupGate";
import { ROUTES } from "@/types/routes";

const BOOKING_FORM_URL = "https://www.drtutor.uk/learn#book";

const LoginPage = lazy(() => import("@/pages/auth/LoginPage"));
const SignupPage = lazy(() => import("@/pages/auth/SignupPage"));
const ForgotPasswordPage = lazy(
  () => import("@/pages/auth/ForgotPasswordPage"),
);
const ResetPasswordPage = lazy(() => import("@/pages/auth/ResetPasswordPage"));
const ActivatePage = lazy(() => import("@/pages/auth/ActivatePage"));
const MagicLinkPage = lazy(() => import("@/pages/auth/MagicLinkPage"));
const EmailPreferencesPage = lazy(() => import("@/pages/email-preferences/EmailPreferencesPage"));
const ChangePasswordPage = lazy(
  () => import("@/pages/auth/ChangePasswordPage"),
);
const OnboardingPage = lazy(() => import("@/pages/onboarding/OnboardingPage"));
const BookAssessmentPage = lazy(() => import("@/pages/onboarding/BookAssessmentPage"));
const CareersPage = lazy(() => import("@/pages/careers/CareersPage"));
const WorkspacePausedPage = lazy(() => import("@/pages/tenant/WorkspacePausedPage"));
const WorkspaceNotFoundPage = lazy(() => import("@/pages/tenant/WorkspaceNotFoundPage"));

/** Signed-in pages by path. Each loader is shared by `lazy` and the preloader;
 *  the browser caches the module, so a preloaded page renders at once. */
const APP_PAGES = {
  [ROUTES.HOME]: () => import("@/pages/dashboard/DashboardPage"),
  [ROUTES.LESSONS]: () => import("@/pages/lessons/LessonsPage"),
  [ROUTES.HOMEWORK]: () => import("@/pages/homework/HomeworkPage"),
  [ROUTES.SUBMISSIONS]: () => import("@/pages/submissions/SubmissionsPage"),
  [ROUTES.STUDENTS]: () => import("@/pages/students/StudentsPage"),
  [ROUTES.TUTORS]: () => import("@/pages/tutors/TutorsPage"),
  [ROUTES.RESOURCES]: () => import("@/pages/resources/ResourcesPage"),
  [ROUTES.MESSAGES]: () => import("@/pages/messages/MessagesPage"),
  [ROUTES.PROGRESS]: () => import("@/pages/progress/ProgressPage"),
  [ROUTES.REPORTS]: () => import("@/pages/reports/ReportsPage"),
  [ROUTES.BILLING]: () => import("@/pages/billing/BillingPage"),
  [ROUTES.PIPELINE]: () => import("@/pages/pipeline/PipelinePage"),
  [ROUTES.PROFILE]: () => import("@/pages/profile/ProfilePage"),
  [ROUTES.DATA_CONTROL]: () => import("@/pages/data-control/DataControlPage"),
  [ROUTES.AVAILABILITY]: () => import("@/pages/availability/AvailabilityPage"),
  [ROUTES.SETTINGS]: () => import("@/pages/settings/SettingsPage"),
  [ROUTES.SETUP]: () => import("@/pages/setup/SetupPage"),
  [ROUTES.WHATS_NEW]: () => import("@/pages/whats-new/WhatsNewPage"),
  [ROUTES.CHANGELOG]: () => import("@/pages/changelog/ChangelogPage"),
} as const;

const DashboardPage = lazy(APP_PAGES[ROUTES.HOME]);
const LessonsPage = lazy(APP_PAGES[ROUTES.LESSONS]);
const HomeworkPage = lazy(APP_PAGES[ROUTES.HOMEWORK]);
const SubmissionsPage = lazy(APP_PAGES[ROUTES.SUBMISSIONS]);
const StudentsPage = lazy(APP_PAGES[ROUTES.STUDENTS]);
const TutorsPage = lazy(APP_PAGES[ROUTES.TUTORS]);
const ResourcesPage = lazy(APP_PAGES[ROUTES.RESOURCES]);
const MessagesPage = lazy(APP_PAGES[ROUTES.MESSAGES]);
const ProgressPage = lazy(APP_PAGES[ROUTES.PROGRESS]);
const ReportsPage = lazy(APP_PAGES[ROUTES.REPORTS]);
const BillingPage = lazy(APP_PAGES[ROUTES.BILLING]);
const PipelinePage = lazy(APP_PAGES[ROUTES.PIPELINE]);
const ProfilePage = lazy(APP_PAGES[ROUTES.PROFILE]);
const DataControlPage = lazy(APP_PAGES[ROUTES.DATA_CONTROL]);
const AvailabilityPage = lazy(APP_PAGES[ROUTES.AVAILABILITY]);
const SettingsPage = lazy(APP_PAGES[ROUTES.SETTINGS]);
const SetupPage = lazy(APP_PAGES[ROUTES.SETUP]);
const WhatsNewPage = lazy(APP_PAGES[ROUTES.WHATS_NEW]);
const ChangelogPage = lazy(APP_PAGES[ROUTES.CHANGELOG]);

// A saved session means a signed-in page is next: fetch its code now.
if (tokenStorage.getAccess()) {
  const current = APP_PAGES[window.location.pathname.slice(basePath.length) as keyof typeof APP_PAGES];
  void current?.().catch(() => undefined);
}

/** Preloads every signed-in page while the browser is idle. Renders nothing. */
function PreloadAppPages() {
  useEffect(() => {
    const run = () => {
      for (const load of Object.values(APP_PAGES)) void load().catch(() => undefined);
    };
    // Safari has no requestIdleCallback; a short timeout stands in for it.
    if (typeof window.requestIdleCallback === "function") {
      const id = window.requestIdleCallback(run, { timeout: 4000 });
      return () => window.cancelIdleCallback(id);
    }
    const id = setTimeout(run, 1500);
    return () => clearTimeout(id);
  }, []);
  return null;
}

function FrameWithPreload() {
  return (
    <>
      <AppFrame />
      <PreloadAppPages />
      <SetupGate />
    </>
  );
}

export function App() {
  // The saved light / dark preference applies to every surface, auth and
  // public pages included (brand-redesign-docs/07-OPEN-QUESTIONS, item 2).
  useAppliedTheme();

  const { phase } = useTenantState();
  const signupOn = useTenant().loginOptions.signup;

  // TL-052 / TL-053: an unknown host, or a paused workspace, replaces the app.
  // A paused workspace still lets people sign in (the owner needs to, to
  // update payment); every other path shows the paused screen.
  if (phase === "not_found") {
    return (
      <BrowserRouter basename={basePath}>
        <Suspense fallback={<RouteFallback />}>
          <WorkspaceNotFoundPage />
        </Suspense>
      </BrowserRouter>
    );
  }
  if (phase === "paused") {
    return (
      <BrowserRouter basename={basePath}>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route element={<GuestRoute />}>
              <Route path={ROUTES.LOGIN} element={<LoginPage />} />
            </Route>
            <Route path="*" element={<WorkspacePausedPage />} />
          </Routes>
        </Suspense>
        <Toaster />
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter basename={basePath}>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Public. No auth check. */}
          {/* Public, not guest-only: the emailed onboarding link names its
              own account, and a signed-in parent with no child yet is sent
              here by the portal gate to finish Step 2. */}
          <Route path={ROUTES.ONBOARDING} element={<OnboardingPage />} />
          <Route path={ROUTES.BOOK_ASSESSMENT} element={<BookAssessmentPage />} />
          <Route element={<RequireFeature name="careers" />}>
            <Route path={ROUTES.CAREERS} element={<CareersPage />} />
          </Route>
          {/* Public, not guest-only: an invite link names its own account via
              the token, so it has to work even when someone else is already
              signed in on this device. Redeeming replaces that session. */}
          <Route path={ROUTES.ACTIVATE} element={<ActivatePage />} />
          {/* Public for the same reason: the sign-in link names its own
              account and replaces any session already on this device. */}
          <Route path={ROUTES.MAGIC_LINK} element={<MagicLinkPage />} />
          <Route path={ROUTES.EMAIL_PREFERENCES} element={<EmailPreferencesPage />} />

          {/* Guest only */}
          <Route element={<GuestRoute />}>
            <Route path={ROUTES.LOGIN} element={<LoginPage />} />
            {signupOn ? <Route path={ROUTES.SIGNUP} element={<SignupPage />} /> : null}
            <Route
              path={ROUTES.FORGOT_PASSWORD}
              element={<ForgotPasswordPage />}
            />
            <Route
              path={ROUTES.RESET_PASSWORD}
              element={<ResetPasswordPage />}
            />
          </Route>

          {/* Signed-in only. One AppFrame for every page, mounted once as a
              layout route so the frame persists across navigation. */}
          <Route element={<ProtectedRoute />}>
            <Route element={<FrameWithPreload />}>
              <Route path={ROUTES.HOME} element={<DashboardPage />} />
              <Route path={ROUTES.SUBMISSIONS} element={<SubmissionsPage />} />
              <Route path={ROUTES.TUTORS} element={<TutorsPage />} />
              <Route path={ROUTES.STUDENTS} element={<StudentsPage />} />
              <Route path={ROUTES.LESSONS} element={<LessonsPage />} />
              <Route path={ROUTES.HOMEWORK} element={<HomeworkPage />} />
              <Route path={ROUTES.RESOURCES} element={<ResourcesPage />} />
              <Route path={ROUTES.MESSAGES} element={<MessagesPage />} />
              <Route path={ROUTES.PROGRESS} element={<ProgressPage />} />
              <Route path={ROUTES.REPORTS} element={<ReportsPage />} />
              <Route path={ROUTES.BILLING} element={<BillingPage />} />
              <Route path={ROUTES.PIPELINE} element={<PipelinePage />} />
              <Route path={ROUTES.PROFILE} element={<ProfilePage />} />
              <Route path={ROUTES.DATA_CONTROL} element={<DataControlPage />} />
              <Route path={ROUTES.AVAILABILITY} element={<AvailabilityPage />} />
              <Route path={ROUTES.SETTINGS} element={<SettingsPage />} />
              <Route path={`${ROUTES.SETTINGS}/:section`} element={<SettingsPage />} />
              <Route path={ROUTES.SETUP} element={<SetupPage />} />
              <Route path={ROUTES.WHATS_NEW} element={<WhatsNewPage />} />
              <Route element={<RequireFeature name="loopdesk_support" />}>
                <Route path={ROUTES.CHANGELOG} element={<ChangelogPage />} />
              </Route>
              <Route path={ROUTES.CHANGE_PASSWORD} element={<ChangePasswordPage />} />
            </Route>
          </Route>

          {/* Retired booking forms (decision D11). The marketing site and
              the legacy platform linked to both for months, so keep those
              links landing on the one booking form rather than on sign in. */}
          <Route
            path={ROUTES.APPLY}
            element={<ExternalRedirect to={BOOKING_FORM_URL} />}
          />
          <Route
            path={ROUTES.REGISTER}
            element={<ExternalRedirect to={BOOKING_FORM_URL} />}
          />

          <Route path="*" element={<Navigate to={ROUTES.HOME} replace />} />
        </Routes>
      </Suspense>
      <Toaster />
      <FileViewerHost />
    </BrowserRouter>
  );
}
